import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {ComboBox, FlexColumn, NumberField, Text, TextField} from '@forecast-it/design-system';
import {HiddenLabel} from '../HiddenLabel';
import {AddRowButton, EmptyEstimates, FieldLabel, RemoveRowButton} from './EstimateLineParts';
import {blankExpenseEstimate, ExpenseEstimateDraft, Option, toNullableNumber} from './baselineTemplateDraft';

// The largest amount the company service stores. Negative amounts are allowed.
const MONEY_LIMIT = 999999999.99;

const Grid = Styled.div`
	display: grid;
	grid-template-columns: minmax(160px, 1fr) minmax(160px, 1fr) 130px 130px 40px;
	gap: 8px 12px;
	align-items: start;
`;

type Props = {
	phaseIndex: number;
	rows: ExpenseEstimateDraft[];
	categories: Option[];
	currency: string;
	fieldError: (path: string) => string | undefined;
	onChange: (rows: ExpenseEstimateDraft[], changedPath?: string) => void;
};

export const ExpenseEstimateLines = ({phaseIndex, rows, categories, currency, fieldError, onChange}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const categoryHeaderId = `baseline-template-phase-${phaseIndex}-category-header`;
	const cy = (rowIndex: number, part: string) => `baseline-template-phase-${phaseIndex}-expense-${rowIndex}-${part}`;
	const update = (key: string, patch: Partial<ExpenseEstimateDraft>, field: string) =>
		onChange(
			rows.map(row => (row.key === key ? {...row, ...patch} : row)),
			`${key}.${field}`
		);
	const money = (rowIndex: number, row: ExpenseEstimateDraft, field: 'cost' | 'revenue') => (
		<FieldLabel>
			<HiddenLabel>{formatMessage({id: `deliverable.columns.${field}`})}</HiddenLabel>
			<NumberField
				data-cy={cy(rowIndex, field)}
				value={row[field] ?? Number.NaN}
				onChange={(value: number) => update(row.key, {[field]: toNullableNumber(value)}, field)}
				allowEmpty
				format={NumberField.FORMAT.CURRENCY}
				currencyCode={currency}
				minValue={-MONEY_LIMIT}
				maxValue={MONEY_LIMIT}
				minimumFractionDigits={2}
				maximumFractionDigits={2}
			/>
		</FieldLabel>
	);

	return (
		<FlexColumn gap="s">
			{rows.length === 0 ? (
				<EmptyEstimates message={formatMessage({id: 'deliverable.no_expense_estimates_no_deliverables'})} />
			) : (
				<Grid>
					<Text color="auxiliary">{formatMessage({id: 'deliverable.columns.name'})}</Text>
					<span id={categoryHeaderId}>
						<Text color="auxiliary">{formatMessage({id: 'deliverable.columns.category'})}</Text>
					</span>
					<Text color="auxiliary">{formatMessage({id: 'deliverable.columns.cost'})}</Text>
					<Text color="auxiliary">{formatMessage({id: 'deliverable.columns.revenue'})}</Text>
					<span />
					{rows.map((row, rowIndex) => {
						const removeLabelId = `baseline-template-remove-label-${row.key}`;
						return (
							<React.Fragment key={row.key}>
								<div data-field-path={`${row.key}.name`}>
									<TextField
										aria-label={formatMessage({id: 'deliverable.columns.name'})}
										data-cy={cy(rowIndex, 'name')}
										value={row.name}
										onValueChange={(value: string) => update(row.key, {name: value}, 'name')}
										hasError={!!fieldError(`${row.key}.name`)}
										errorText={fieldError(`${row.key}.name`)}
									/>
								</div>
								<div data-field-path={`${row.key}.expenseCategoryId`}>
									<ComboBox
										aria-labelledby={categoryHeaderId}
										data-cy={cy(rowIndex, 'category')}
										items={categories}
										value={row.expenseCategoryId}
										onChange={(id: string | null) =>
											update(row.key, {expenseCategoryId: id ?? null}, 'expenseCategoryId')
										}
										menuTrigger="focus"
										width={ComboBox.WIDTH.FLEX}
										hasError={!!fieldError(`${row.key}.expenseCategoryId`)}
										errorText={fieldError(`${row.key}.expenseCategoryId`)}
									>
										{(option: Option) => (
											<ComboBox.Item textValue={option.label}>{option.label}</ComboBox.Item>
										)}
									</ComboBox>
								</div>
								{money(rowIndex, row, 'cost')}
								{money(rowIndex, row, 'revenue')}
								<RemoveRowButton
									labelId={removeLabelId}
									label={formatMessage({id: 'deliverable.columns.delete_expense_estimate'})}
									data-cy={cy(rowIndex, 'remove')}
									onPress={() => onChange(rows.filter(candidate => candidate.key !== row.key))}
								/>
							</React.Fragment>
						);
					})}
				</Grid>
			)}
			<AddRowButton
				label={formatMessage({id: 'deliverable.add_expenses_estimate'})}
				data-cy={`baseline-template-phase-${phaseIndex}-add-expense`}
				onPress={() => onChange([...rows, blankExpenseEstimate()])}
			/>
		</FlexColumn>
	);
};
